/**
 * FilterableColumnHeader Component
 *
 * Column header with compact filter icon button and sorting capability.
 * Clicking the filter icon opens a dropdown for filter selection.
 */

import * as React from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown, Check, Filter, Calendar } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/base/Popover';
import { DateRangePicker, DateRangePreset, DateRange } from './DateRangePicker';
import { cn } from '@utils/cn';
import { FilterOption, SortDirection } from './types';

export interface FilterableColumnHeaderProps {
  /** Column header text */
  children: React.ReactNode;

  /** Whether column is sortable */
  sortable?: boolean;
  /** Sort key for API */
  sortKey?: string;
  /** Current sort state */
  currentSort?: { key: string | null; direction: SortDirection };
  /** Sort change handler */
  onSort?: (key: string) => void;

  /** Whether column has filter */
  filterable?: boolean;
  /** Filter type */
  filterType?: 'select' | 'date-range';
  /** Filter options for select type */
  filterOptions?: FilterOption[];
  /** Current filter value */
  filterValue?: string;
  /** Filter change handler */
  onFilterChange?: (value: string | undefined) => void;

  /** For date range filters: current preset */
  datePreset?: DateRangePreset;
  /** For date range filters: custom range */
  dateRange?: DateRange;
  /** Date range change handler */
  onDateRangeChange?: (preset: DateRangePreset, range?: DateRange) => void;

  /** Column alignment */
  align?: 'left' | 'center' | 'right';

  /** Optional custom class for the header text */
  headerClassName?: string;

  /** Hide the filter icon while keeping header text visible */
  hideFilterIcon?: boolean;
}

export function FilterableColumnHeader({
  children,
  sortable = false,
  sortKey,
  currentSort,
  onSort,
  filterable = false,
  filterType = 'select',
  filterOptions = [],
  filterValue,
  onFilterChange,
  datePreset = 'all',
  dateRange,
  onDateRangeChange,
  align = 'center',
  headerClassName,
  hideFilterIcon = false,
}: FilterableColumnHeaderProps) {
  const [filterOpen, setFilterOpen] = React.useState(false);

  // Check if sorted/filtered
  const isSorted = currentSort?.key === sortKey;
  const sortDirection = isSorted ? currentSort?.direction : null;
  const hasFilter = filterValue && filterValue !== 'all';
  const hasDateFilter = datePreset !== 'all';
  const isActive = isSorted || hasFilter || hasDateFilter;

  // Alignment
  const alignmentClasses = {
    left: 'justify-start text-left',
    center: 'justify-center text-center',
    right: 'justify-end text-right',
  };

  // Handle sort click
  const handleSortClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (sortable && sortKey && onSort) {
      onSort(sortKey);
    }
  };

  // Handle filter change
  const handleFilterSelect = (value: string) => {
    onFilterChange?.(value === 'all' ? undefined : value);
    setFilterOpen(false);
  };

  // Render sort icon
  const renderSortIcon = () => {
    if (!sortable) return null;

    const Icon =
      sortDirection === 'asc' ? ArrowUp : sortDirection === 'desc' ? ArrowDown : ArrowUpDown;

    return (
      <button
        onClick={handleSortClick}
        className={cn(
          'flex-shrink-0 rounded p-0.5 transition-colors hover:bg-gray-200',
          isSorted ? 'text-[#061a61]' : 'text-gray-400'
        )}
        title={`Sort ${sortDirection === 'asc' ? 'descending' : 'ascending'}`}
      >
        <Icon className="h-3 w-3" />
      </button>
    );
  };

  // If not filterable, render simple header with optional sort
  if (!filterable) {
    return (
      <div className={cn('flex items-center gap-1', alignmentClasses[align])}>
        <p
          className={cn(
            headerClassName
              ? headerClassName
              : cn('font-hanken text-sm font-medium', isActive ? 'text-[#061a61]' : 'text-gray-700')
          )}
        >
          {children}
        </p>
        {renderSortIcon()}
      </div>
    );
  }

  // Date range filter - icon button that opens DateRangePicker in popover
  if (filterType === 'date-range' && onDateRangeChange) {
    return (
      <div className={cn('flex items-center gap-1', alignmentClasses[align])}>
        <p
          className={cn(
            headerClassName
              ? headerClassName
              : cn(
                  'font-hanken text-sm font-medium',
                  hasDateFilter ? 'text-[#061a61]' : 'text-gray-700'
                )
          )}
        >
          {children}
        </p>
        {renderSortIcon()}
        {!hideFilterIcon && (
          <Popover open={filterOpen} onOpenChange={setFilterOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  'flex-shrink-0 rounded p-1 transition-colors',
                  hasDateFilter
                    ? 'bg-[#061a61] text-white'
                    : 'text-gray-400 hover:bg-gray-200 hover:text-gray-600'
                )}
                title="Filter by date"
              >
                <Calendar className="h-3.5 w-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="center" className="w-auto p-0">
              <DateRangePicker
                preset={datePreset}
                customRange={dateRange}
                onChange={(preset, range) => {
                  onDateRangeChange(preset, range);
                  if (preset !== 'custom') {
                    setFilterOpen(false);
                  }
                }}
                size="md"
              />
            </PopoverContent>
          </Popover>
        )}
      </div>
    );
  }

  // Select filter - icon button that opens dropdown
  return (
    <div className={cn('flex items-center gap-1', alignmentClasses[align])}>
      <p
        className={cn(
          headerClassName
            ? headerClassName
            : cn('font-hanken text-sm font-medium', isActive ? 'text-[#061a61]' : 'text-gray-700')
        )}
      >
        {children}
      </p>
      {renderSortIcon()}

      {/* Filter icon button */}
      {!hideFilterIcon && (
        <Popover open={filterOpen} onOpenChange={setFilterOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                'flex-shrink-0 rounded p-1 transition-colors',
                hasFilter
                  ? 'bg-[#061a61] text-white'
                  : 'text-gray-400 hover:bg-gray-200 hover:text-gray-600'
              )}
              title="Filter"
            >
              <Filter className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>

          <PopoverContent align="center" className="w-48 p-2">
            <div className="space-y-0.5">
              {/* All option */}
              <button
                type="button"
                onClick={() => handleFilterSelect('all')}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 font-hanken text-sm transition-colors',
                  !hasFilter ? 'bg-[#061a61]/10 text-[#061a61]' : 'text-gray-700 hover:bg-gray-100'
                )}
              >
                <Check className={cn('h-3.5 w-3.5', hasFilter && 'invisible')} />
                All
              </button>

              {/* Filter options */}
              {filterOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleFilterSelect(option.value)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 font-hanken text-sm transition-colors',
                    filterValue === option.value
                      ? 'bg-[#061a61]/10 text-[#061a61]'
                      : 'text-gray-700 hover:bg-gray-100'
                  )}
                >
                  <Check
                    className={cn('h-3.5 w-3.5', filterValue !== option.value && 'invisible')}
                  />
                  {option.label}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

export default FilterableColumnHeader;
